import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ColDef, GridApi, GridReadyEvent, RowClassRules } from 'ag-grid-enterprise';
import { AgGridAngular } from 'ag-grid-angular';
import { AG_GRID_LOCALE_DE } from 'src/app/_helpers/aggrid.locale.de';
import { dateFormatter } from 'src/app/_helpers/aggrid-renderer';
import { Article, DeliveryNote } from 'src/app/_models';
import { ArticleService } from 'src/app/_services';
import { AgGridThemeService } from 'src/app/_services/ag-grid-theme.service';
import { environment } from 'src/environments/environment';
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { TicketSubhashArticles } from 'src/app/_models/trip_subhash_articles';
import { ModalService } from 'src/app/_services/modal.service';
import { xidToEpc } from 'src/app/_helpers/xid';
import { XidRekeyDialogComponent } from '../_shared/xid-rekey-dialog/xid-rekey-dialog.component';

@Component({
  imports: [CommonModule, RouterModule, AgGridAngular],
  selector: 'hawk-article-show',
  templateUrl: './article-show.component.html',
  styleUrls: ['./article-show.component.scss'],
  host: {
    class: 'app-page'
  }
})
export class ArticleShowComponent implements OnInit {
  constructor(
    private articleService: ArticleService,
    private route: ActivatedRoute,
    private router: Router,
    private modalService: ModalService
  ) { }

  public readonly Object = Object;

  public articleId: number | string | string;
  public article: Article;
  public articles: any[];
  public deliveryNotes: DeliveryNote[];
  public tickets: TicketSubhashArticles[];
  public histories: any[] = []
  env = environment;

  public locale = AG_GRID_LOCALE_DE;
  public hawkTheme = inject(AgGridThemeService).getTheme();
  public isCheckingShipcloudTrackingStatus: boolean = false;
  public rowSelection: "single" | "multiple" = "multiple"
  public rowClassRules: RowClassRules = {
    // if tech on site, color row
    'tech-on-site': (params) => {
      if (params?.data === undefined) return "...";
      return params.data.tech_on_site
    },
  }

  // AG-Grid: Letzte Änderungen
  private historyGridApi!: GridApi;
  public historyDefaultColDef: ColDef = { filter: true, sortable: true };
  public historyColDefs: ColDef[] = [
    {
      field: 'event',
      headerName: 'Aktion',
      width: 80,
      sortable: false,
      filter: false,
    },
    {
      field: 'created_at',
      headerName: 'Zeitpunkt',
      valueFormatter: dateFormatter(),
      width: 140,
    },
    {
      field: 'user',
      headerName: 'Benutzer',
      width: 140,
    },
    {
      field: 'attribute',
      headerName: 'Feld',
      flex: 1,
      filter: 'agTextColumnFilter',
    },
    {
      field: 'old_value',
      headerName: 'Vorher',
      flex: 1,
      sortable: false,
      filter: false,
      cellClass: 'text-red-500',
    },
    {
      field: 'new_value',
      headerName: 'Nachher',
      flex: 1,
      sortable: false,
      filter: false,
      cellClass: 'text-green-500',
    },
  ];

  onHistoryGridReady(event: GridReadyEvent) {
    this.historyGridApi = event.api;
  }

  ngOnInit(): void {
    this.articleId = parseInt(this.route.snapshot.paramMap.get('id'));
    // this.loadarticle();
    this.loadArticle();
    this.loadDeliveryNotes();
    this.loadTickets();
    this.loadHistories();
  }

  private loadHistories() {
    this.articleService.getHistories(this.articleId).subscribe(
      {
        next: (response) => {
          this.histories = this.flattenHistories(response);
        }, error: (error) => {
          console.log('error', error);
        }
      }
    )
  }

  private flattenHistories(histories: any[]): any[] {
    const rows: any[] = [];
    for (const entry of histories) {
      const changes = entry.changes;
      if (!changes || Object.keys(changes).length === 0) {
        rows.push({ event: entry.event, created_at: entry.created_at, user: entry.user, attribute: '-', old_value: '-', new_value: '-' });
        continue;
      }
      for (const key of Object.keys(changes)) {
        const change = changes[key];
        rows.push({
          event: entry.event,
          created_at: entry.created_at,
          user: entry.user,
          attribute: key,
          old_value: change[1] ?? '-',
          new_value: change[0] ?? '-',
        });
      }
    }
    return rows;
  }

  private loadTickets() {
    this.articleService.getTickets(this.articleId).subscribe(
      {
        next: (response) => {
          this.tickets = response;
        }, error: (error) => {
          console.log('error', error);
        }
      }
    )
  }

  private loadDeliveryNotes() {
    this.articleService.getDeliveryNotes(this.articleId).subscribe(
      {
        next: (response) => {
          this.deliveryNotes = response.sort((a, b) => new Date(b.note_date).getTime() - new Date(a.note_date).getTime());
        }, error: (error) => {
          console.log('error', error);
        }
      }
    )
  }

  private loadArticle() {
    this.articleService.show(this.articleId).subscribe(
      {
        next: (response: any) => {
          this.article = response;
        },
        error: (error) => {
          console.log('error', error);
        }
      }
    )
  }

  /** EPC des Etiketts; fehlt das Server-Feld, aus der XID gerechnet */
  public get epc(): string | null {
    return this.article?.epc || xidToEpc(this.article?.xid);
  }

  /** Neue XID vergeben (bentomax/hawk#605); danach Artikel und Historie neu laden */
  public openRekeyDialog() {
    const ref = this.modalService.open(XidRekeyDialogComponent, {
      inputs: {
        ownerType: 'article',
        ownerId: this.article.id,
        currentXid: this.article.xid ?? null,
      },
      minHeight: 'auto',
    });
    ref.closed.subscribe((saved) => {
      if (saved === true) {
        this.loadArticle();
        this.loadHistories();
      }
    });
  }

  public onDelete() {
    if(confirm('Möchten Sie den Artikel wirklich löschen?')) {
      this.articleService.delete(this.articleId).subscribe(
        {
          next: () => {
            this.router.navigate(['/', 'artikel', 'suche', 'index']);
          },
          error: (error) => {
            console.log('errors', error);
          }
        }
      )
    }
  }

}
